import { ChevronLeft, Eye, EyeOff, Info } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { InputWithLabel } from "@/pages/components/input-label";
import PasswordInput from "@/pages/components/input-password";
import BackNavigator from "@/pages/components/back-navigation";
import { useForm } from "@tanstack/react-form";
import { registerPageSchema } from "@/lib/form-schema";

import type { IRegisterResponse } from "@/hooks/use-register";
import { useMRegister } from "@/hooks/use-register";
import { PhoneInput } from "@/pages/components/input-phone";
import InputDate from "@/pages/components/input-date";
import { useGroupProvider } from "@/context/group-provider";
import { Input } from "@/pages/components/input";
import { useNavigate } from "react-router-dom";
import { formatDateToMMDDYYYY } from "@/lib/date-utils";
import ScreenRouteNames from "@/constants/screen-route-names";
import { useState } from "react";
import {
  AlignError,
  PASSWORD_REQUIREMENTS_MESSAGE,
  REGISTRATION_FIELDS,
  USERNAME_REQUIREMENTS_MESSAGE,
} from "@/constants";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import type { OptionalErrorType } from "@/types";
import InfoToolTip from "@/pages/components/info-tooltip";
import IDENTIFIERS from "@/constants/test_id";

export type IRegisterFormValues = {
  userName: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  zipCode: string;
  dob: Date | undefined;
  email: string;
  phone: string;
  ssnLastFour?: string | undefined;
  divisionId?: string;
  ssn?: string;
  memberId?: string;
};

type RegisterProps = {
  onBackToDisclaimer: () => void;
};

const RadioButton = ({ selected }: { selected: boolean }) => (
  <div className="mr-2 flex size-4 items-center justify-center rounded-full border-2 border-black">
    {selected && (
      <div className="flex size-2 items-center justify-center rounded-full bg-white">
        <div className="size-2 rounded-full bg-primary" />
      </div>
    )}
  </div>
);

const Register = ({ onBackToDisclaimer }: RegisterProps) => {
  const registerQuery = useMRegister();
  const { groupData } = useGroupProvider();
  const registrationFieldsLookup = groupData?.registrationFieldsLookup;

  const navigate = useNavigate();
  const [selectedField, setSelectedField] = useState<string>(
    REGISTRATION_FIELDS.MEMBER_ID,
  );
  const [zip, setZip] = useState("");
  const [memberId, setMemberId] = useState("");
  const [ssn, setSsn] = useState("");
  // Option Field Error States
  const [zipCodeError, setZipCodeError] = useState<OptionalErrorType>({
    isError: false,
    errorMsg: "",
  });
  const [memberIdError, setMemberIdError] = useState<OptionalErrorType>({
    isError: false,
    errorMsg: "",
  });
  const [ssnError, setSsnError] = useState<OptionalErrorType>({
    isError: false,
    errorMsg: "",
  });
  const [isSSNVisible, setIsSSNVisible] = useState<boolean>(false);
  const Icon = isSSNVisible ? Eye : EyeOff;
  const showOptionalFields =
    registrationFieldsLookup?.[REGISTRATION_FIELDS.MEMBER_ID]?.visible === 1 ||
    registrationFieldsLookup?.[REGISTRATION_FIELDS.SSN]?.visible === 1;

  const handleSelect = (field: string) => {
    setSelectedField(field);
    if (field === REGISTRATION_FIELDS.MEMBER_ID) {
      setSsnError({ isError: false, errorMsg: "" });
      setSsn("");
    } else {
      setMemberIdError({ isError: false, errorMsg: "" });
      setMemberId("");
    }
  };

  const handleTextChange = (field: string, value: string) => {
    handleSelect(field);
    if (field === REGISTRATION_FIELDS.MEMBER_ID) {
      setMemberId(value);
    } else {
      setSsn(value);
    }
  };

  const form = useForm({
    defaultValues: {
      userName: "",
      password: "",
      confirmPassword: "",
      firstName: "",
      lastName: "",
      zipCode: "",
      dob: undefined,
      email: "",
      phone: "",
      ssnLastFour: groupData?.is_ssn_required ? "" : undefined,
      divisionId: groupData?.division_id ?? "",
      ssn: "",
      memberId: "",
    },

    onSubmit: async ({ value }) => {
      if (
        !zipCodeError.isError &&
        (showOptionalFields
          ? !memberIdError.isError && !ssnError.isError
          : true)
      ) {
        const registerInfo: IRegisterResponse = {
          groupId: groupData?.group_id ?? "",
          firstname: value.firstName.trim(),
          lastname: value.lastName.trim(),
          password: value.password.trim(),
          zip,
          dob: String(formatDateToMMDDYYYY(value.dob)),
          username: value.userName.trim(),
          email: value?.email.trim(),
          phone: value.phone.trim(),
          loginGroupId: groupData?.entered_id ?? "",
          divisionId: groupData?.division_id ?? "",
          ssnSuffix: ssn.trim(),
          employeeId: memberId.trim(),
        };
        const registrationDetails = Object.fromEntries(
          Object.entries(registerInfo).filter(([_, v]) => v !== ""),
        );
        registerQuery.mutate(registrationDetails as IRegisterResponse, {
          onSuccess: (data) => {
            if (!data.success) return;
            navigate(`/${ScreenRouteNames.VERIFY_OTP.path}`, {
              state: {
                registerInfo: {
                  signature: data.signature,
                  ...registerInfo,
                },
              },
            });
          },
        });
      }
    },
  });

  const isValidZip = () => {
    if (zip === "") {
      return { isError: true, errorMsg: "ZIP code cannot be empty." };
    }
    if (!/^\d+$/.test(zip)) {
      return { isError: true, errorMsg: "Zip code must contain digits only." };
    }
    if (!/^\d{5}$/.test(zip)) {
      return { isError: true, errorMsg: "ZIP code must be 5 digits." };
    }
    return { isError: false, errorMsg: "" };
  };

  const isValidMemberId = () => {
    if (memberId === "") {
      return { isError: true, errorMsg: "Member ID cannot be empty." };
    }
    if (memberId.length > 50) {
      return {
        isError: true,
        errorMsg: "Member ID cannot exceed 50 characters.",
      };
    }
    return { isError: false, errorMsg: "" };
  };

  const isValidSSN = () => {
    if (ssn === "") {
      return { isError: true, errorMsg: "SSN cannot be empty." };
    }
    if (!/^\d+$/.test(ssn)) {
      return { isError: true, errorMsg: "SSN must contain digits only." };
    }
    if (!/^\d{4}$/.test(ssn)) {
      return { isError: true, errorMsg: "SSN must be exactly 4 digits." };
    }
    return { isError: false, errorMsg: "" };
  };

  const onZipCodeFieldBlur = () => {
    if (
      registrationFieldsLookup?.[REGISTRATION_FIELDS.ZIP].visible === 1 &&
      isValidZip().isError
    ) {
      setZipCodeError(isValidZip());
    }
  };

  const onMemberIdFieldBlur = () => {
    if (
      selectedField === REGISTRATION_FIELDS.MEMBER_ID &&
      isValidMemberId().isError
    ) {
      setMemberIdError(isValidMemberId());
    } else {
      setMemberIdError({ errorMsg: "", isError: false });
    }
  };

  const onSsnFieldBlur = () => {
    if (selectedField === REGISTRATION_FIELDS.SSN && isValidSSN().isError) {
      setSsnError(isValidSSN());
    } else {
      setSsnError({ errorMsg: "", isError: false });
    }
  };

  const checkOptionalFields = () => {
    onZipCodeFieldBlur();
    onMemberIdFieldBlur();
    onSsnFieldBlur();
  };

  return (
    <main className="flex h-full flex-col items-center justify-center space-y-10">
      <BackNavigator
        title="Account Registration"
        left={
          <Button
            variant="ghost"
            aria-label="Go back"
            className="left-0 h-auto w-fit bg-transparent p-0 hover:bg-transparent hover:shadow-none focus:bg-transparent focus:shadow-none active:bg-transparent"
            onClick={() => {
              const hasDisclaimer =
                Boolean(groupData?.registrationDisclaimer) &&
                groupData.registrationDisclaimer.trim().length > 0;

              if (hasDisclaimer) {
                onBackToDisclaimer();
                return;
              }

              if (window.history.length > 1) navigate(-1);
              else navigate("/nav-error");
            }}
            data-testid={IDENTIFIERS.REGISTER.BACK_BUTTON}
          >
            <ChevronLeft size={20} />
          </Button>
        }
      />

      <section className="w-full overflow-y-scroll px-8 pb-24 pt-5">
        <form
          method="POST"
          className="flex w-full flex-col space-y-2"
          onSubmit={(e) => {
            checkOptionalFields();
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className="flex flex-col space-y-3">
            <Label id="groupCode">Group Code</Label>
            <Input
              id="groupCode"
              placeholder="Enter Group Code"
              className="border-0 bg-gray-100 focus-visible:ring-gray-200"
              value={groupData?.entered_id}
              data-testid={IDENTIFIERS.REGISTER.GROUP_CODE_INPUT}
              readOnly
            />
          </div>

          {/* UserName */}
          {registrationFieldsLookup?.[REGISTRATION_FIELDS.USERNAME]!.visible ===
          1 ? (
            <form.Field
              name="userName"
              validators={{
                onBlur: ({ value }) => {
                  const result =
                    registerPageSchema.shape.userName.safeParse(value);
                  return result.success
                    ? undefined
                    : result.error.errors[0].message;
                },
              }}
            >
              {(field) => (
                <div className="flex flex-row items-center space-x-2">
                  <InputWithLabel
                    id="userName"
                    label={
                      registrationFieldsLookup?.[REGISTRATION_FIELDS.USERNAME]
                        ?.label
                    }
                    tooltip={USERNAME_REQUIREMENTS_MESSAGE}
                    placeholder={
                      registrationFieldsLookup?.[REGISTRATION_FIELDS.USERNAME]
                        ?.label
                    }
                    value={field.state.value}
                    onChange={(newValue) => field.handleChange(newValue)}
                    onBlur={() => field.handleBlur()}
                    error={field.state.meta.errors[0]}
                    data-testid={IDENTIFIERS.REGISTER.USERNAME_INPUT}
                  />
                </div>
              )}
            </form.Field>
          ) : null}

          {/* Password */}
          {registrationFieldsLookup?.[REGISTRATION_FIELDS.PASSWORD]!.visible ===
          1 ? (
            <form.Field
              name="password"
              validators={{
                onBlur: ({ value }) => {
                  const result =
                    registerPageSchema.shape.password.safeParse(value);
                  return result.success
                    ? undefined
                    : result.error.errors[0].message;
                },
              }}
            >
              {(field) => (
                <div className="space-y-1">
                  <div className="flex flex-row items-center space-x-2">
                    <Label htmlFor="password">
                      {
                        registrationFieldsLookup?.[REGISTRATION_FIELDS.PASSWORD]
                          ?.label
                      }
                    </Label>
                    <InfoToolTip info={PASSWORD_REQUIREMENTS_MESSAGE} />
                  </div>
                  <PasswordInput
                    id="password"
                    placeholder={
                      registrationFieldsLookup?.[REGISTRATION_FIELDS.PASSWORD]
                        ?.label
                    }
                    value={field.state.value}
                    onChange={(newValue) => field.handleChange(newValue)}
                    onBlur={() => field.handleBlur()}
                    error={field.state.meta.errors[0]}
                    data-testid={IDENTIFIERS.REGISTER.PASSWORD_INPUT}
                  />
                </div>
              )}
            </form.Field>
          ) : null}

          {/* Confirm Password */}
          {registrationFieldsLookup?.[REGISTRATION_FIELDS.CONFIRM_PASSWORD]!
            .visible === 1 ? (
            <form.Field
              name="confirmPassword"
              validators={{
                onBlur: ({ value, fieldApi }) => {
                  const result =
                    registerPageSchema.shape.confirmPassword.safeParse(value);
                  if (!result.success) {
                    return result.error.issues[0]?.message;
                  }
                  const password = fieldApi.form.getFieldValue("password");
                  if (password && value !== password) {
                    return "Passwords do not match";
                  }
                  return undefined;
                },
                onChangeListenTo: ["password"],
              }}
            >
              {(field) => (
                <div className="space-y-1">
                  <Label htmlFor="confirmPassword">
                    {
                      registrationFieldsLookup?.[
                        REGISTRATION_FIELDS.CONFIRM_PASSWORD
                      ]?.label
                    }
                  </Label>
                  <PasswordInput
                    id="confirmPassword"
                    value={field.state.value}
                    placeholder={
                      registrationFieldsLookup?.[
                        REGISTRATION_FIELDS.CONFIRM_PASSWORD
                      ]?.label
                    }
                    onChange={(newValue) => field.handleChange(newValue)}
                    onBlur={() => field.handleBlur()}
                    error={field.state.meta.errors[0]}
                    data-testid={IDENTIFIERS.REGISTER.CONFIRM_PASSWORD_INPUT}
                  />
                </div>
              )}
            </form.Field>
          ) : null}

          <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-1 xl:grid-cols-2">
            {/* FirstName */}
            {registrationFieldsLookup?.[REGISTRATION_FIELDS.FIRST_NAME]!
              .visible === 1 ? (
              <form.Field
                name="firstName"
                validators={{
                  onBlur: ({ value }) => {
                    const result =
                      registerPageSchema.shape.firstName.safeParse(value);
                    return result.success
                      ? undefined
                      : result.error.errors[0].message;
                  },
                }}
              >
                {(field) => (
                  <div className="flex flex-row items-center space-x-2">
                    <InputWithLabel
                      id="firstName"
                      label={
                        registrationFieldsLookup?.[
                          REGISTRATION_FIELDS.FIRST_NAME
                        ].label
                      }
                      placeholder={
                        registrationFieldsLookup?.[
                          REGISTRATION_FIELDS.FIRST_NAME
                        ].label
                      }
                      value={field.state.value}
                      onChange={(newValue) => field.handleChange(newValue)}
                      onBlur={() => field.handleBlur()}
                      error={field.state.meta.errors[0]}
                      data-testid={IDENTIFIERS.REGISTER.FIRST_NAME_INPUT}
                    />
                  </div>
                )}
              </form.Field>
            ) : null}
            {/* LastName */}
            {registrationFieldsLookup?.[REGISTRATION_FIELDS.LAST_NAME]!
              .visible === 1 ? (
              <form.Field
                name="lastName"
                validators={{
                  onBlur: ({ value }) => {
                    const result =
                      registerPageSchema.shape.lastName.safeParse(value);
                    return result.success
                      ? undefined
                      : result.error.errors[0].message;
                  },
                }}
              >
                {(field) => (
                  <div className="flex flex-row items-center space-x-2">
                    <InputWithLabel
                      id="lastName"
                      label={
                        registrationFieldsLookup?.[
                          REGISTRATION_FIELDS.LAST_NAME
                        ]?.label
                      }
                      placeholder={
                        registrationFieldsLookup?.[
                          REGISTRATION_FIELDS.LAST_NAME
                        ]?.label
                      }
                      onChange={(newValue) => field.handleChange(newValue)}
                      onBlur={() => field.handleBlur()}
                      error={field.state.meta.errors[0]}
                      data-testid={IDENTIFIERS.REGISTER.LAST_NAME_INPUT}
                    />
                  </div>
                )}
              </form.Field>
            ) : null}
          </div>

          {/* Zip Code */}
          {registrationFieldsLookup?.[REGISTRATION_FIELDS.ZIP]!.visible ===
          1 ? (
            <>
              {/* 3.3.2 Labels or Instructions — A: the <Label> had no htmlFor and the
                  field no id, so the label was orphaned and the field unnamed — a screen
                  reader announced an anonymous edit box on the registration form.
                  autoComplete also closes 1.3.5 Identify Input Purpose — AA. */}
              <Label htmlFor="register-zip-code">
                {registrationFieldsLookup?.[REGISTRATION_FIELDS.ZIP].label}
              </Label>
              <form.Field name="zipCode">
                {() => (
                  <div className="relative flex flex-row items-center">
                    <Input
                      id="register-zip-code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="postal-code"
                      className="w-full border-0 bg-gray-100 focus-visible:ring-gray-200"
                      placeholder={
                        registrationFieldsLookup?.[REGISTRATION_FIELDS.ZIP]
                          .label
                      }
                      value={zip}
                      maxLength={5}
                      onBlur={onZipCodeFieldBlur}
                      onFocus={() =>
                        setZipCodeError({ isError: false, errorMsg: "" })
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        setZip(value.trim());
                      }}
                      data-testid={IDENTIFIERS.REGISTER.ZIP_CODE_INPUT}
                    />
                    {zipCodeError.isError ? (
                      <HoverCard>
                        <HoverCardTrigger asChild>
                          <Info
                            className="absolute right-2 top-3 cursor-pointer text-[11px] text-red-400"
                            size={16}
                          />
                        </HoverCardTrigger>
                        <HoverCardContent align={AlignError.CENTER}>
                          <em className="text-xs text-red-400">
                            {zipCodeError.errorMsg}
                          </em>
                        </HoverCardContent>
                      </HoverCard>
                    ) : null}
                  </div>
                )}
              </form.Field>
            </>
          ) : null}

          {/* DOB */}
          {registrationFieldsLookup?.[REGISTRATION_FIELDS.DOB]!.visible ===
          1 ? (
            <div>
              <form.Field
                name="dob"
                validators={{
                  onChange: ({ value }) => {
                    const result =
                      registerPageSchema.shape.dob.safeParse(value);
                    return result.success
                      ? undefined
                      : result.error.errors[0].message;
                  },
                }}
              >
                {(field) => (
                  <InputDate
                    id="dob"
                    label={
                      registrationFieldsLookup?.[REGISTRATION_FIELDS.DOB].label
                    }
                    placeholder={
                      registrationFieldsLookup?.[REGISTRATION_FIELDS.DOB].label
                    }
                    value={field.state.value}
                    onChange={(newDate) => {
                      field.handleChange(newDate);
                    }}
                    error={field.state.meta.errors[0]}
                    data-testid={IDENTIFIERS.REGISTER.DOB_INPUT}
                  />
                )}
              </form.Field>
            </div>
          ) : null}
          {/* Email */}
          {registrationFieldsLookup?.[REGISTRATION_FIELDS.EMAIL]!.visible ===
          1 ? (
            <div>
              <form.Field
                name="email"
                validators={{
                  onBlur: ({ value }) => {
                    const result =
                      registerPageSchema.shape.email.safeParse(value);
                    return result.success
                      ? undefined
                      : result.error.errors[0].message;
                  },
                }}
              >
                {(field) => (
                  <div className="flex flex-row items-center space-x-2">
                    <InputWithLabel
                      id="email"
                      label={
                        registrationFieldsLookup?.[REGISTRATION_FIELDS.EMAIL]
                          .label
                      }
                      placeholder={
                        registrationFieldsLookup?.[REGISTRATION_FIELDS.EMAIL]
                          .label
                      }
                      value={field.state.value}
                      onChange={(newValue) => field.handleChange(newValue)}
                      onBlur={() => field.handleBlur()}
                      error={field.state.meta.errors[0]}
                      data-testid={IDENTIFIERS.REGISTER.EMAIL_INPUT}
                    />
                  </div>
                )}
              </form.Field>
            </div>
          ) : null}
          {/* Phone */}
          {registrationFieldsLookup?.[REGISTRATION_FIELDS.PHONE]!.visible ===
          1 ? (
            <div>
              <form.Field
                name="phone"
                validators={{
                  onBlur: ({ value }) => {
                    const result =
                      registerPageSchema.shape.phone.safeParse(value);
                    return result.success
                      ? undefined
                      : result.error.errors[0].message;
                  },
                }}
              >
                {(field) => (
                  <div className="flex w-full flex-col gap-3">
                    <Label>
                      {
                        registrationFieldsLookup?.[REGISTRATION_FIELDS.PHONE]
                          .label
                      }
                    </Label>
                    <PhoneInput
                      id="phone"
                      className="w-full"
                      defaultCountry="US"
                      value={field.state.value}
                      onChange={(newValue) => field.handleChange(newValue)}
                      onBlur={() => field.handleBlur()}
                      error={field.state.meta.errors[0]}
                      data-testid={IDENTIFIERS.REGISTER.PHONE_NUMBER_INPUT}
                    />
                  </div>
                )}
              </form.Field>
            </div>
          ) : null}
          {Boolean(
            registrationFieldsLookup?.[REGISTRATION_FIELDS.MEMBER_ID]?.visible,
          ) &&
            Boolean(
              registrationFieldsLookup?.[REGISTRATION_FIELDS.SSN]?.visible,
            ) && (
              <p className="py-3 text-sm font-medium leading-none">
                Include One of the Below:
              </p>
            )}
          {/* Member ID */}
          {Boolean(
            registrationFieldsLookup?.[REGISTRATION_FIELDS.MEMBER_ID]!.visible,
          ) && (
            <div>
              <form.Field name="memberId">
                {() => (
                  <div>
                    {/*
                      2.1.1 Keyboard — A / 4.1.2 Name, Role, Value — A / 1.3.1 — A.
                      Choosing whether to identify yourself by Member ID or SSN was a
                      <div onClick>: not in the tab order, no radio role, no checked state.
                      A member who cannot use a mouse could not register for the portal at
                      all. A real radio input carries the role, the checked state and
                      arrow-key group navigation; it is visually hidden rather than removed
                      so the existing RadioButton graphic can stay, and `peer` drives a
                      focus ring so the focus indicator is visible (2.4.7 — AA).
                    */}
                    <label className="flex cursor-pointer items-center">
                      <input
                        type="radio"
                        name="register-identifier"
                        value={REGISTRATION_FIELDS.MEMBER_ID}
                        checked={
                          selectedField === REGISTRATION_FIELDS.MEMBER_ID
                        }
                        onChange={() =>
                          handleSelect(REGISTRATION_FIELDS.MEMBER_ID)
                        }
                        className="peer sr-only"
                      />
                      {Boolean(
                        registrationFieldsLookup?.[REGISTRATION_FIELDS.SSN]!
                          .visible,
                      ) && (
                        <span className="rounded-full peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2">
                          <RadioButton
                            selected={
                              selectedField === REGISTRATION_FIELDS.MEMBER_ID
                            }
                          />
                        </span>
                      )}

                      <span className="overflow-hidden text-ellipsis text-sm font-medium leading-none">
                        {
                          registrationFieldsLookup?.[
                            REGISTRATION_FIELDS.MEMBER_ID
                          ].label
                        }
                      </span>
                    </label>
                    <div className="relative flex flex-row items-center">
                      {/* 3.3.2 Labels or Instructions — A: the label above belongs to the
                          radio option, not to this field, which had only a placeholder —
                          and placeholders vanish on the first keystroke. */}
                      <Input
                        aria-label={
                          registrationFieldsLookup?.[
                            REGISTRATION_FIELDS.MEMBER_ID
                          ].label
                        }
                        type="text"
                        className="mt-3 border-0 bg-gray-100 focus-visible:ring-gray-200"
                        placeholder={
                          registrationFieldsLookup?.[
                            REGISTRATION_FIELDS.MEMBER_ID
                          ].label
                        }
                        value={memberId}
                        maxLength={50}
                        onBlur={onMemberIdFieldBlur}
                        onChange={(e) =>
                          handleTextChange(
                            REGISTRATION_FIELDS.MEMBER_ID,
                            e.target.value,
                          )
                        }
                        data-testid={IDENTIFIERS.REGISTER.EMPLOYEE_ID_INPUT}
                      />
                      {memberIdError.isError ? (
                        <HoverCard>
                          <HoverCardTrigger asChild>
                            <Info
                              className="absolute right-2 top-6 cursor-pointer text-[11px] text-red-400"
                              size={16}
                            />
                          </HoverCardTrigger>
                          <HoverCardContent align={AlignError.CENTER}>
                            <em className="text-xs text-red-400">
                              {memberIdError?.errorMsg}
                            </em>
                          </HoverCardContent>
                        </HoverCard>
                      ) : null}
                    </div>
                  </div>
                )}
              </form.Field>
            </div>
          )}
          {/* SSN */}
          {Boolean(
            registrationFieldsLookup?.[REGISTRATION_FIELDS.SSN]!.visible,
          ) && (
            <div>
              <form.Field name="ssn">
                {() => (
                  <>
                    {/* 2.1.1 Keyboard — A / 4.1.2 — A: see the Member ID option above —
                        the two together form the identifier radio group. */}
                    <label className="flex cursor-pointer items-center">
                      <input
                        type="radio"
                        name="register-identifier"
                        value={REGISTRATION_FIELDS.SSN}
                        checked={selectedField === REGISTRATION_FIELDS.SSN}
                        onChange={() => handleSelect(REGISTRATION_FIELDS.SSN)}
                        className="peer sr-only"
                      />
                      {Boolean(
                        registrationFieldsLookup?.[
                          REGISTRATION_FIELDS.MEMBER_ID
                        ]!.visible,
                      ) && (
                        <span className="rounded-full peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2">
                          <RadioButton
                            selected={selectedField === REGISTRATION_FIELDS.SSN}
                          />
                        </span>
                      )}

                      <span className="overflow-hidden text-ellipsis text-sm font-medium leading-none">
                        {
                          registrationFieldsLookup?.[REGISTRATION_FIELDS.SSN]
                            .label
                        }
                      </span>
                    </label>
                    <div className="relative flex flex-row items-center">
                      {/* 3.3.2 Labels or Instructions — A: the label above belongs to the
                          radio option, not to this field. The expected format is stated in
                          the name because a 4-digit SSN field is otherwise ambiguous. */}
                      <Input
                        aria-label={`${
                          registrationFieldsLookup?.[REGISTRATION_FIELDS.SSN]
                            .label
                        } (last 4 digits)`}
                        type={isSSNVisible ? "text" : "password"}
                        maxLength={4}
                        inputMode="numeric"
                        className="mt-3 border-0 bg-gray-100 focus-visible:ring-gray-200"
                        placeholder={
                          registrationFieldsLookup?.[REGISTRATION_FIELDS.SSN]
                            .label
                        }
                        value={ssn}
                        onBlur={onSsnFieldBlur}
                        onChange={(e) =>
                          handleTextChange(
                            REGISTRATION_FIELDS.SSN,
                            e.target.value,
                          )
                        }
                        data-tesid={IDENTIFIERS.REGISTER.SSN_INPUT}
                      />
                      {ssnError.isError ? (
                        <HoverCard>
                          <HoverCardTrigger asChild>
                            <Info
                              className="absolute right-10 top-6 cursor-pointer text-[11px] text-red-400"
                              size={16}
                            />
                          </HoverCardTrigger>
                          <HoverCardContent align={AlignError.CENTER}>
                            <em className="text-xs text-red-400">
                              {ssnError.errorMsg}
                            </em>
                          </HoverCardContent>
                        </HoverCard>
                      ) : null}
                      <Icon
                        onClick={() => setIsSSNVisible(!isSSNVisible)}
                        className="absolute right-3 top-6 size-4 cursor-pointer text-black"
                      />
                    </div>
                  </>
                )}
              </form.Field>
            </div>
          )}
          {/* Register Button */}
          <div className="absolute bottom-0 left-0 z-10 flex w-full items-center justify-center bg-white p-4">
            <form.Subscribe selector={(state) => [state.canSubmit]}>
              {([canSubmit]) => {
                return (
                  <Button
                    type="submit"
                    size="lg"
                    variant="default"
                    className="my-2 w-40 bg-primary text-sm hover:bg-primary-selected sm:px-2"
                    disabled={
                      !canSubmit ||
                      registerQuery.isPending ||
                      zipCodeError.isError ||
                      (showOptionalFields
                        ? ssnError.isError || memberIdError.isError
                        : false)
                    }
                    data-testid={IDENTIFIERS.REGISTER.SUBMIT_BUTTON}
                  >
                    {registerQuery.isPending ? "Registering..." : "Register"}
                  </Button>
                );
              }}
            </form.Subscribe>
          </div>
        </form>
      </section>
    </main>
  );
};

export default Register;
